import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Subject, Observable, forkJoin } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import * as CryptoJS from 'crypto-js';
import { environment } from '@environments/environment';
import { Client } from '@classes/client';
import { SitesService } from '@services/sites.service';
import * as FileSaver from 'file-saver';

@Injectable({
  providedIn: 'root'
})
export class ClientsService {
  clientsListChanged = new Subject<Client[]>();
  private baseUrl: string = environment.apiUrl + 'clients';
  private kooveaUrl: string = environment.apiUrl + 'koovea';
  private clients: Client[];

  constructor(
    private http: HttpClient,
    private sitesSrv: SitesService
  ) { }

  getList(): Observable<Client[]> {
    return this.http.get<Client[]>(this.baseUrl)
      .pipe(
        map(clients => {
          this.clients = clients.map(c => Client.fromObject(c));
          this.clients.sort((a, b) => a.name.localeCompare(b.name));
          this.clientsListChanged.next([...this.clients]);
          return [...this.clients];
        })
      );
  }

  /** Agences ayant au moins un donneur d'ordre en transmission only. */
  getTransmissionOnlyList(): Observable<Client[]> {
    return forkJoin([this.getList(), this.sitesSrv.getTransmissionOnlyList()])
      .pipe(
        map(([clients, sites]) => {
          const clientIds = new Set<string>();
          sites.forEach(site => site.clients?.forEach(client => clientIds.add(client.id)));
          return clients.filter(client => clientIds.has(client.id));
        })
      );
  }

  put(client: Client): Observable<Client> {
    return this.http.put<Client>(this.baseUrl, client)
      .pipe(
        tap(editedClient => {
          editedClient = Client.fromObject(editedClient);
          this.clients = this.clients.map(c => c.id === editedClient.id ? editedClient : c);
          this.clientsListChanged.next([...this.clients]);
        })
      );
  }

  putAdminClient(object: any, idClient: string): Observable<Client> {
    return this.http.put<any>(`${this.baseUrl}/${idClient}/contact`, object)
      .pipe(
        tap(editedClient => {
          editedClient = Client.fromObject(editedClient);
          this.clients = this.clients.map(c => c.id === editedClient.id ? editedClient : c);
          this.clientsListChanged.next([...this.clients]);
        })
      );
  }

  post(client: Client): Observable<Client> {
    return this.http.post<Client>(this.baseUrl, client)
      .pipe(
        tap(createdClient => {
          createdClient = Client.fromObject(createdClient);
          this.clients.push(createdClient);
          this.clientsListChanged.next([...this.clients]);
        })
      );
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`)
      .pipe(
        tap(() => {
          this.clients = this.clients.filter(m => m.id !== id);
          this.clientsListChanged.next([...this.clients]);
        })
      );
  }

  putPhoto(file, idClient: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.put<any>(`${this.baseUrl}/${idClient}/logo`, formData).pipe(map(res => this.getList()));
  }

  downloadLogo(id: string): Observable<Blob> {
    const url = `${this.baseUrl}/${id}/logo `;

    return this.http.get(url, { observe: 'response', responseType: 'blob' })
      .pipe(map(res => this.saveFile(res)));
  }

  // getLogo(id: string): Observable<Blob> {
  //   const url = `${this.baseUrl}/${id}/logo `;

  //   return this.http.get(url, { observe: 'response', responseType: 'blob' })
  //   .pipe(
  //     map(clients => {
  //         return clients
  //     })
  // }

  updateClientsSubject(editedClient: Client) {
    editedClient = Client.fromObject(editedClient);
    this.clients = this.clients.map(c => c.id === editedClient.id ? editedClient : c);
    this.clientsListChanged.next([...this.clients]);
  }

  getLogo(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${id}/logo`, {
      observe: 'response', responseType: 'blob'
    })
      .pipe(map(res => res.body));
  }

  private saveFile(res: HttpResponse<Blob>): Blob {
    FileSaver.saveAs(res.body, res.headers.get('filename'));
    return res.body;
  }

  /*
  KOOVEA login
  */

  getKooveaLogin(clientId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/${clientId}/koovea`)
      .pipe(
        map(kooveaLogin => {
          return kooveaLogin;
        })
      );
  }

  postKooveaLogin(form): Observable<any> {
    form.password = this.cryptString(form.password);
    return this.http.post(this.kooveaUrl, form)
      .pipe(
        tap(createdLogin => {
          return createdLogin;
        })
      );
  }

  putKooveaLogin(form): Observable<any> {
    form.password = this.cryptString(form.password);
    return this.http.put(this.kooveaUrl, form)
      .pipe(
        tap(createdLogin => {
          return createdLogin;
        })
      );
  }

  private cryptString(stringTocrypt: string): string {
    return CryptoJS.SHA256(stringTocrypt).toString();
  }

}
